import { Form } from "@circle-react-shared/uikit/Form";
import { ErrorTemplate } from "@circle-react/components/shared/ErrorPages/ErrorTemplate";
import { PAYWALL_OFFER_SUBTYPES } from "@circle-react/helpers/paywallOfferHelpers";
import { noop } from "lodash";
import { useEffect, useState } from "react";

import { t } from "@/i18n-js/instance";
import {
  isSessionAbandoned,
  isSessionPending,
} from "@/react/components/Paywalls/Checkout/helpers/sessions/SessionStatusHelper";
import { offerRoute } from "@/react/routes/PaywallCheckout/tanstack_routes/checkout/$checkoutPath/step/$stepId";
import type { CheckoutSession } from "@/react/types/Paywalls/Checkout/Session";
import { checkoutSessionStepStatuses } from "@/react/types/Paywalls/Checkout/Session";

import { useCheckoutSession } from "../../Checkout/contexts/CheckoutSessionContext";
import {
  isStepProcessing,
  isStepActionable,
  isStepPending,
  isStepAbandoned,
  isStepFailed,
} from "../../Checkout/helpers/sessions/StepStatusHelper";
import { useUpdateSessionStep } from "../../Checkout/hooks/sessions/useUpdateSessionStep";
import { firePurchaseForCompletedSteps } from "../helpers/ga4PurchaseHelper";
import { getCommunityRootUrl } from "../helpers/RoutesHelper";
import {
  getStepById,
  getMainPaywallId,
  getCoupon,
  getOfferedPaywall,
  getPaywallOffer,
} from "../helpers/SessionStepQueryHelper";
import { useCheckoutSessionRouteParams } from "../hooks/useCheckoutSessionRouteParams";
import { useGoToNextStep } from "../hooks/useGoToNextStep";
import { OfferPage } from "../pages/OfferPage";
import { AttemptsProvider } from "../pages/OfferPage/AttemptsProvider";
import { OfferPageContextProvider } from "../pages/OfferPage/OfferPageProvider";
import { UpgradeOfferPage } from "../pages/OfferPage/UpgradeOfferPage";
import { UpgradeOfferStatusPage } from "../pages/OfferPage/UpgradeOfferStatusPage";

const redirectToCommunityRoot = (session: CheckoutSession) => {
  window.location.href = getCommunityRootUrl(session);
};

const isReadOnlyUpgradeStatus = (
  session: CheckoutSession | null,
  step: ReturnType<typeof getStepById>,
  isUpgrade: boolean,
  mainPaywallId: number | undefined,
  isVerifyingPayment: boolean,
  hasStartedUpgradeAcceptance: boolean,
) =>
  Boolean(
    session &&
    isSessionPending(session) &&
    step &&
    isStepProcessing(step) &&
    isUpgrade &&
    mainPaywallId !== undefined &&
    !isVerifyingPayment &&
    !hasStartedUpgradeAcceptance,
  );

const shouldNavigateFromStep = (
  step: ReturnType<typeof getStepById>,
  isVerifyingPayment: boolean,
  hasStartedUpgradeAcceptance: boolean,
) =>
  Boolean(
    step &&
    !isStepActionable(step) &&
    !isStepFailed(step) &&
    !isVerifyingPayment &&
    !hasStartedUpgradeAcceptance,
  );

export const OfferRoute = () => {
  const { stepId } = offerRoute.useParams();
  const { paymentValidationParams } = useCheckoutSessionRouteParams();
  const { ga4FiredStepIdsRef, session } = useCheckoutSession();
  const { mutate: updateSessionStep, isPending: isUpdatingStep } =
    useUpdateSessionStep();
  const { goToNextStep } = useGoToNextStep();
  const [hasStartedUpgradeAcceptance, setHasStartedUpgradeAcceptance] =
    useState(false);

  // Returning from Stripe with payment params means we need to verify payment.
  const isVerifyingPayment = Boolean(
    paymentValidationParams?.paymentIntentId ||
    paymentValidationParams?.invoiceId ||
    paymentValidationParams?.setupIntentId,
  );

  const step = getStepById(session?.steps || [], Number(stepId));
  const mainPaywallId = getMainPaywallId(session);
  const isPending = isStepPending(step);

  useEffect(() => {
    if (
      session &&
      isSessionPending(session) &&
      step &&
      mainPaywallId &&
      isPending
    ) {
      updateSessionStep({
        stepId: step.id,
        paywallId: mainPaywallId,
        status: checkoutSessionStepStatuses.offered,
      });
    }
  }, [session, step, mainPaywallId, isPending, updateSessionStep]);

  const paywall = getOfferedPaywall(step);
  const paywallOffer = getPaywallOffer(step);
  const couponCode = getCoupon(step);
  const isUpgrade = paywallOffer?.subtype === PAYWALL_OFFER_SUBTYPES.UPGRADE;
  const isReadOnlyProcessingUpgrade = isReadOnlyUpgradeStatus(
    session,
    step,
    isUpgrade,
    mainPaywallId,
    isVerifyingPayment,
    hasStartedUpgradeAcceptance,
  );
  const shouldNavigate = shouldNavigateFromStep(
    step,
    isVerifyingPayment,
    hasStartedUpgradeAcceptance,
  );

  useEffect(() => {
    if (!session || !step || !shouldNavigate) return;

    firePurchaseForCompletedSteps(session, ga4FiredStepIdsRef);
    if (!goToNextStep(session, step.id)) redirectToCommunityRoot(session);
  }, [ga4FiredStepIdsRef, goToNextStep, session, shouldNavigate, step]);

  if (
    !session ||
    isUpdatingStep ||
    (step &&
      isStepProcessing(step) &&
      !isVerifyingPayment &&
      !isReadOnlyProcessingUpgrade &&
      !hasStartedUpgradeAcceptance)
  ) {
    return <OfferPage.Skeleton />;
  }

  if (!step || !paywall) {
    return (
      <ErrorTemplate
        errorCode={404}
        title={t("paywall_checkout.error_boundary.title")}
      />
    );
  }

  const defaultPrice =
    paywall.prices.find(price => price.preferred) || paywall.prices[0];

  if (isUpgrade && mainPaywallId === undefined) {
    return (
      <ErrorTemplate
        errorCode={404}
        title={t("paywall_checkout.error_boundary.title")}
      />
    );
  }

  if (isSessionAbandoned(session) || isStepAbandoned(step)) {
    redirectToCommunityRoot(session);
    return null;
  }

  if (
    isReadOnlyProcessingUpgrade &&
    session &&
    step &&
    mainPaywallId !== undefined
  ) {
    return (
      <Form
        defaultValues={{}}
        onSubmit={noop}
        className="size-full"
        formClassName="size-full"
      >
        <UpgradeOfferStatusPage
          checkoutPaywallId={mainPaywallId}
          checkoutSessionId={session.id}
          stepId={step.id}
        />
      </Form>
    );
  }

  // A `failed` step (e.g. a cancelled 3DS attempt on an upsell) is not
  // "actionable" for next-step purposes, but on refresh the CURRENT step
  // should re-render itself -- not route the member away -- so they can
  // retry or decline. Fall through to the normal render below.
  //
  // If step is not actionable AND we're NOT verifying payment, navigate away
  // If we're verifying payment, let AttemptsProvider handle verification and navigation
  if (shouldNavigate) return null;

  return (
    <Form
      defaultValues={{
        paywall_price_id: isUpgrade ? defaultPrice?.id : defaultPrice.id,
        coupon_code: couponCode,
      }}
      onSubmit={noop}
      className="size-full"
      formClassName="size-full"
    >
      <OfferPageContextProvider
        paywall={paywall}
        stepId={stepId}
        isUpgrade={isUpgrade}
        paywallCustomization={
          paywallOffer?.paywall_customization || paywall?.paywall_customization
        }
      >
        {isUpgrade && mainPaywallId !== undefined ? (
          <UpgradeOfferPage
            key={stepId}
            checkoutSessionId={session.id}
            communityId={session.community.id}
            checkoutPaywallId={mainPaywallId}
            invoiceId={paymentValidationParams?.invoiceId}
            onAcceptanceStateChange={setHasStartedUpgradeAcceptance}
            paymentIntentId={paymentValidationParams?.paymentIntentId}
            paywall={paywall}
            setupIntentId={paymentValidationParams?.setupIntentId}
            stepId={Number(stepId)}
          />
        ) : (
          <AttemptsProvider key={stepId}>
            <OfferPage />
          </AttemptsProvider>
        )}
      </OfferPageContextProvider>
    </Form>
  );
};
